import axios from "axios";
import { DEVICE_TYPE } from "../utils/Constant";
import { STATUS_CODES } from "../utils/statusCode";
import { getErrorMessage } from "../utils/errorCodes";
import Toster from "../utils/Toaster";
import { store } from "../store/store";
import { removeUserData } from "../store/slices/userSlice";

const API_BASE_URL = process.env.REACT_APP_PUBLIC_BASE_URL;

const apiInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "device-type": DEVICE_TYPE.TYPE,
    "device-id": process.env.REACT_APP_DEVICE_ID,
    "device-token": process.env.REACT_APP_DEVICE_TOKEN,
    "api-key": process.env.REACT_APP_API_KEY,
  },
});

const clearExpiredAdminSession = () => {
  localStorage.removeItem("userToken");
  localStorage.removeItem("persist:root");
  store.dispatch(removeUserData({}));

  if (window.location.pathname !== "/login") {
    window.location.replace("/login");
  }
};

// Add interceptors to include token and handle response codes
apiInstance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("userToken"); // Fetch token from local storage
    if (token) {
      config.headers["access-token"] = token; // Add the 'access-token' header
      // config.headers.access-token = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

apiInstance.interceptors.response.use(
  (response) => {
    // Some APIs may return 304 Not Modified when caching/ETag is used.
    // Treat 304 as a valid response here so callers can decide how to handle it.
    if (response?.status === 304) {
      return response;
    }

    const code = response?.data?.code;
    if (code === STATUS_CODES.VALIDATION_ERROR) {
      const msg = response?.data?.message;
      Toster(msg, "error");
    } else if (code !== STATUS_CODES.SUCCESS) {
      Toster(getErrorMessage(code), "error");
      return Promise.reject(response);
    }
    return response;
  },
  (error) => {
    const isNotAuthorized =
      error.response?.status === 401 ||
      error.response?.data?.code == STATUS_CODES.INVALID_TOKEN ||
      error.response?.data?.code == STATUS_CODES.TOKEN_IS_REQUIRED ||
      error.response?.data?.code == STATUS_CODES.ACCESS_DENIED ||
      error.response?.data?.code == STATUS_CODES.UNAUTHORIZED;

    // if (error.response?.data?.code) {
    //   Toster(t(getErrorMessage(error.response?.data?.code)), "error");
    //   return Promise.reject(error);
    // } else {
    //   const statusCode =
    //     error.response?.status || STATUS_CODES.INTERNAL_SERVER_ERROR;
    //   Toster(t(getErrorMessage(statusCode)), "error");
    //   return Promise.reject(error);
    // }
    // 104,103,110,401
    if (isNotAuthorized) {
      clearExpiredAdminSession();
      return Promise.reject(error);
    }

    if (error.response?.data?.code) {
      Toster(getErrorMessage(error.response?.data?.code), "error");
    }

    return Promise.reject(error);
  }
);

// API methods
export const getMethod = async (url, params) => {
  // Add cache-control headers to avoid conditional requests from returning 304
  // and accept 304 as a non-error via validateStatus so response interceptor
  // (and callers) can handle it explicitly.
  const response = await apiInstance.get(url, {
    params,
    headers: {
      "Cache-Control": "no-cache",
      Pragma: "no-cache",
    },
    validateStatus: (status) => status >= 200 && status < 400,
  });
  return response.data;
};

export const postMethod = async (url, data) => {
  const response = await apiInstance.post(url, data);
  return response.data;
};

export const putMethod = async (url, data) => {
  const response = await apiInstance.put(url, data);
  return response.data;
};

export const deleteMethod = async (url, data) => {
  const response = await apiInstance.delete(url, { data });
  return response.data;
};
